import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import emailjs from 'emailjs-com';
import flower from '../img/flowers3.png';
import ornament from '../img/ornament.png';

type FormData = {
  name: string;
  surname: string;
  phone: string;
  attendance: string;
  menu?: string;
  allergies?: string;
};

const ConfirmationSection: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<FormData>();

  const attendance = watch('attendance');
  const [specialMenu, setSpecialMenu] = useState(false); // Track checkbox state

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await emailjs.send(
        'service_3jvdjpu',
        'template_0oxcg4u',
        {
          ...data,
          attendance: data.attendance === 'true',
          menu: data.menu || 'No',
        },
        process.env.REACT_APP_EMAIL_API_KEY
      );
      if (data.attendance === 'true') {
        alert('Enviaste tu repuesta! Nos vemos el 15 de marzo! 🎉');
      } else {
        alert('Formulario enviado exitosamente 🎉');
      }
      reset();
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      alert('Ocurrió un error al enviar el formulario. Inténtalo de nuevo.');
    }
  };

  return (
    <section
      id="rsvp"
      className="bg-[#F7F4EF] lg:px-0 md:py-16 xs:py-0 sm:py-0 flex justify-center"
    >
      <div className="relative lg:bg-white md:bg-white sm:bg-[#F7F4EF] lg:shadow-lg md:shadow-lg sm:shadow-none xs:shadow-none max-w-screen-md w-full">
        <img
          src={flower}
          alt="Left Decoration"
          className="hide-on-mobile absolute left-0 top-3/4 transform -translate-y-1/4 w-24 lg:w-32"
          style={{ left: '-128px' }}
        />
        <img
          src={flower}
          alt="Right Decoration"
          className="hide-on-mobile absolute right-0 top-1/4 transform -translate-y-3/4 rotate-180 w-24 lg:w-32"
          style={{ right: '-128px' }}
        />
        <h2 className="text-title text-center text-[#B94665] px-auto pt-8">
          RSVP
        </h2>
        {/* Separator */}
        <div className="mt-4">
          <img
            src={ornament}
            alt="Ornament"
            className="mx-auto transform scale-x-[1] ornament-size h-auto "
          />
        </div>
        <div className="mt-8 px-8 pb-8">
          <p className="mt-4 text-center sm:center-justify text-lg text-gray-500">
            Este día es especial para nosotros, y más aún si tú estás presente.
          </p>
          <p className="mt-9 italic font-semibold text-center sm:center-justify text-gray-500">
            Por favor confirma antes del 15 de enero.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <span className="w-full">
                <input
                  {...register('name', { required: 'Nombre es obligatorio' })}
                  placeholder="Nombre"
                  className="border border-[#B94665] p-3 w-full focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50"
                />
                {errors.name && (
                  <p className="text-red-500">{errors.name.message}</p>
                )}
              </span>
              <span className="w-full">
                <input
                  {...register('surname', {
                    required: 'Apellido es obligatorio',
                  })}
                  placeholder="Apellido"
                  className="border border-[#B94665] p-3 w-full focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50"
                />
                {errors.surname && (
                  <p className="text-red-500">{errors.surname.message}</p>
                )}
              </span>
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <span className="w-full">
                <input
                  {...register('phone', {
                    required: 'Teléfono es obligatorio',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Solo se permiten números',
                    },
                    minLength: {
                      value: 10,
                      message: 'El número debe ser válido',
                    },
                    maxLength: {
                      value: 10,
                      message: 'El número debe ser válido',
                    },
                    setValueAs: (value) => value.trim(),
                  })}
                  placeholder="Teléfono"
                  className="border border-[#B94665] p-3 w-full focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50"
                />
                {errors.phone && (
                  <p className="text-red-500">{errors.phone.message}</p>
                )}
              </span>
            </div>

            <div className="text-center text-gray-500">
              <p className="mb-2 font-semibold">¿Contaremos con tu compañía?</p>
              <div className="flex items-center justify-center">
                <label className="flex items-center justify-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="true"
                    {...register('attendance', {
                      required: 'Selecciona una opción',
                    })}
                    className="appearance-none w-5 h-5 border border-[#B94665] rounded-full checked:bg-[#B94665] checked:border-[#B94665] focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50 relative checked:after:content-[''] checked:after:absolute checked:after:top-[5px] checked:after:left-[5px] checked:after:w-2 checked:after:h-2 checked:after:bg-white checked:after:rounded-full transition-all duration-200"
                  />
                  <span className="mr-4" />
                  Sí asistiré
                </label>
                <label className="ml-4 flex items-center justify-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="false"
                    {...register('attendance')}
                    className="appearance-none w-5 h-5 border border-[#B94665] rounded-full checked:bg-[#B94665] checked:border-[#B94665] focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50 relative checked:after:content-[''] checked:after:absolute checked:after:top-[5px] checked:after:left-[5px] checked:after:w-2 checked:after:h-2 checked:after:bg-white checked:after:rounded-full transition-all duration-200"
                  />
                  <span className="mr-4" />
                  No asistiré
                </label>
              </div>
            </div>
            {errors.attendance && (
              <p className="text-red-500">{errors.attendance.message}</p>
            )}

            {attendance === 'true' && (
              <>
                <div className="text-center text-gray-500">
                  <p className="mb-2 mt-6 font-semibold">
                    ¿Requieres un menú especial?
                  </p>
                  <label className="flex items-center justify-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={specialMenu}
                      onChange={() => setSpecialMenu(!specialMenu)}
                      className="appearance-none w-5 h-5 border border-[#B94665] rounded-md checked:bg-[#B94665] checked:border-[#B94665] focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50 relative checked:after:content-['✔'] checked:after:absolute checked:after:top-[-3px] checked:after:left-[2px] checked:after:text-white"
                    />
                    <span className="mr-4" />
                    Sí, quiero menú especial
                  </label>
                </div>

                {specialMenu && (
                  <div className="text-center text-gray-500">
                    <p className="mb-2 mt-6 font-semibold">
                      Selecciona tu menú:
                    </p>
                    <div className="flex items-center justify-center">
                      <label className="flex items-center justify-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          value="Vegetariano"
                          {...register('menu')}
                          className="appearance-none w-5 h-5 border border-[#B94665] rounded-full checked:bg-[#B94665] checked:border-[#B94665] focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50 relative checked:after:content-[''] checked:after:absolute checked:after:top-[5px] checked:after:left-[5px] checked:after:w-2 checked:after:h-2 checked:after:bg-white checked:after:rounded-full transition-all duration-200"
                        />
                        <span className="mr-4" />
                        Vegetariano
                      </label>
                      <label className="flex items-center justify-center space-x-2 cursor-pointer ml-4">
                        <input
                          type="radio"
                          value="Vegano"
                          {...register('menu')}
                          className="appearance-none w-5 h-5 border border-[#B94665] rounded-full checked:bg-[#B94665] checked:border-[#B94665] focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50 relative checked:after:content-[''] checked:after:absolute checked:after:top-[5px] checked:after:left-[5px] checked:after:w-2 checked:after:h-2 checked:after:bg-white checked:after:rounded-full transition-all duration-200"
                        />
                        <span className="mr-4" />
                        Vegano
                      </label>
                    </div>
                  </div>
                )}

                <div className="text-gray-500 text-center">
                  <p className="mb-2 mt-6 font-semibold">
                    ¿Alguna alergia alimentaria?
                  </p>
                  <textarea
                    {...register('allergies')}
                    placeholder="Describe tus alergias"
                    className="border border-[#B94665] p-3 w-full focus:outline-none focus:ring focus:ring-[#B94665] focus:ring-opacity-50"
                  ></textarea>
                </div>
              </>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                className="bg-[#B94665] text-white px-8 py-3 hover:bg-[#9c3b57] transition duration-300 flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                ) : (
                  'Enviar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ConfirmationSection;
